import React, {useState, useCallback, useRef} from 'react';
import root from 'window-or-global';
import {FeaturesCarouselSlideProps} from './FeaturesCarousel.slide';
import {AccentColorOptions} from 'epic-ue-theme';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import {
    CenteredGrid,
    Eyebrow,
    Hyperlink,
    MinusIcon,
    PlusIcon,
    Typography,
    VerticalSpacer,
    Image
} from '../../elements';
import {subNavHeight} from '../../elements/subnav/Subnav.config';
import styled from 'styled-components';
import {WithCaption, Spotlight} from '../../wraps';
import Grid from '@material-ui/core/Grid';

const Styled = styled.div`
    && {
        .ue-accordion {
            border-radius: 0;
        }
    }
    .ue-accordion {
        background: transparent;
        border-top: 1px solid ${(p) => p.theme.palette.text.hint};
        border-radius: 0;
        border-left: none;
        border-right: none;
        box-shadow: none;
        margin: 0;

        &:last-child {
            border-bottom: 1px solid ${(p) => p.theme.palette.text.hint};
        }

        .explore-links span.link {
            display: block;
        }

        .mui-accordion-details-root,
        .mui-accordion-summary-root {
            padding: 0;
        }

        .mui-accordion-summary-root {
            .content {
                margin: 0;
            }
            margin: 20px 0;
            min-height: unset;
            &:focus-visible {
                outline: 1px solid ${(p) => p.theme.palette.misc.outline};
                outline-offset: 2px;
            }
        }

        .expand-icon {
            padding: 0 8px;
        }

        .expand-icon {
            padding: 0 8px;
        }

        .slide-desc {
            .paragraph {
                color: ${(p) =>
                    p.theme.ifLight(
                        p,
                        p.theme.palette.neutral.steel,
                        p.theme.palette.neutral.stone
                    )};
                ${(p) => p.theme.breakpoints.down('md')} {
                    line-height: 26px;
                }
            }
        }

        .Mui-focusVisible {
            background: unset;
        }

        .edge-end {
            padding: 0;
            margin-right: 13px;
            ${(p) => p.theme.breakpoints.down('sm')} {
                margin-right: 8px;
            }
            transform: rotate(0deg);
            svg {
                position: absolute;
                font-size: 1.7145em;
                ${(p) => p.theme.breakpoints.down('sm')} {
                    font-size: 1.143em;
                }
            }
        }
    }
`;

interface FeaturesCarouselAccordionProps {
    slides: FeaturesCarouselSlideProps[];
    accentColor?: AccentColorOptions;
    isSm?: boolean;
}

export const FeaturesCarouselAccordion = ({
    slides = [],
    accentColor,
    isSm
}: FeaturesCarouselAccordionProps): JSX.Element => {
    const [activeSlide, setActiveSlide] = useState('');
    const [pristine, setPristine] = useState(true);

    const accordionSummary = useRef(null);

    const isInViewport = function (elem) {
        if (!elem) return true;
        const bounding = elem.getBoundingClientRect();
        return (
            bounding.top >= 0 &&
            bounding.left >= 0 &&
            bounding.bottom <= window.innerHeight &&
            bounding.right <= window.innerWidth
        );
    };

    const scrollToItem = (newExpandedItem) => {
        if (root.__server_side_render) return;
        const el = root.document.getElementsByClassName(`panel-${newExpandedItem}`);
        if (el) {
            let element = el[0];
            let offsetTop = 0;
            while (element) {
                offsetTop += element.offsetTop;
                element = element.offsetParent;
            }
            root.scrollTopSkip = location.pathname; //prevent ScrollToTop from resetting position for this page
            root.scrollTo({left: 0, top: offsetTop - subNavHeight, behavior: 'smooth'});
        }
    };

    const handleChange = useCallback(
        (panel) => (event, isExpanded) => {
            setActiveSlide(isExpanded ? panel : '');
            setPristine(false);
            if (isExpanded) {
                //after css transitons done
                //only scroll when accordion summary is off screen
                !isInViewport(accordionSummary.current) &&
                    setTimeout(() => {
                        scrollToItem(panel);
                    }, 500);
            }
        },
        [setActiveSlide]
    );

    return (
        <Styled>
            {slides.map((slide = {}, i) => {
                const {
                    slideName,
                    title,
                    description,
                    caption = {},
                    image = {},
                    links = [],
                    eyebrow = {}
                } = slide;
                const defaultFirstSectionOpen = pristine && i === 0;
                const icon =
                    defaultFirstSectionOpen || activeSlide === slideName ? (
                        <MinusIcon color={accentColor} />
                    ) : (
                        <PlusIcon color={accentColor} />
                    );
                const fullLayout = Boolean(image && image.src && title);

                return (
                    <Accordion
                        className="ue-accordion"
                        key={`accordion-slide-${slideName || title}`}
                        TransitionProps={{unmountOnExit: true}}
                        defaultExpanded={defaultFirstSectionOpen}
                        expanded={slideName === activeSlide || defaultFirstSectionOpen}
                        onChange={handleChange(slideName)}
                        ref={accordionSummary}>
                        <AccordionSummary
                            className={`mui-accordion-summary-root panel-${slideName}`}
                            classes={{
                                expandIcon: 'expand-icon',
                                expanded: 'expanded',
                                content: 'content'
                            }}
                            IconButtonProps={{classes: {edgeEnd: 'edge-end'}}}
                            expandIcon={icon}>
                            <Typography variant="h5">{slideName}</Typography>
                        </AccordionSummary>

                        <Spotlight
                            variant="small"
                            offsetRatioDown={[0.1, 0.2]}
                            accentColor={accentColor}
                            image={image?.src}
                            optMaxWidth={400}>
                            <AccordionDetails className="mui-accordion-details-root">
                                <CenteredGrid alignItems="flex-start" spacing={0}>
                                    {fullLayout ? (
                                        <Grid item xs={12} sm={6}>
                                            <WithCaption {...caption}>
                                                <Image
                                                    {...image}
                                                    ratio="square"
                                                    optMaxWidth={400}
                                                />
                                            </WithCaption>
                                            {isSm ? (
                                                <VerticalSpacer
                                                    variant="custom"
                                                    spacing={5}
                                                    downSmSpacing={4}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </Grid>
                                    ) : (
                                        ''
                                    )}
                                    <Grid item xs={12} sm={fullLayout ? 6 : 12}>
                                        {fullLayout ? (
                                            <>
                                                <Typography variant="h2">{title}</Typography>
                                                <VerticalSpacer
                                                    variant="custom"
                                                    spacing={3}
                                                    downSmSpacing={1}
                                                />
                                            </>
                                        ) : (
                                            ''
                                        )}
                                        {description ? (
                                            <div className="slide-desc">
                                                <Typography variant="p">{description}</Typography>
                                            </div>
                                        ) : (
                                            ''
                                        )}
                                        {eyebrow && eyebrow.text ? (
                                            <>
                                                <VerticalSpacer
                                                    variant="custom"
                                                    spacing={5}
                                                    downSmSpacing={4}
                                                />
                                                <Eyebrow {...eyebrow} />
                                                <VerticalSpacer
                                                    variant="custom"
                                                    spacing={2}
                                                    downSmSpacing={1}
                                                />
                                            </>
                                        ) : (
                                            ''
                                        )}
                                        <div className="explore-links">
                                            {links.map((link, i) => (
                                                <React.Fragment
                                                    key={`explore-link-${link.label}-${i}`}>
                                                    <div className="link">
                                                        <Hyperlink
                                                            {...link}
                                                            accentColor={accentColor}>
                                                            <Typography variant="p">
                                                                {link.label}
                                                            </Typography>
                                                        </Hyperlink>
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                        <VerticalSpacer
                                            variant="custom"
                                            spacing={5}
                                            downSmSpacing={4}
                                        />
                                    </Grid>
                                </CenteredGrid>
                            </AccordionDetails>
                        </Spotlight>
                    </Accordion>
                );
            })}
        </Styled>
    );
};
